import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { PackageSalesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { FileTypePipe } from 'src/app/core/pipes/file-type.pipe';
import { AppConfigService } from 'src/app/core/services/config.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import Adapter from 'src/app/core/utils/file-adapter';
import { FileManagerComponent } from 'src/app/pages/infrastructure/menus/menus-form/upload-file/file-manager/file-manager.component';
import { BaseFormModule } from 'src/app/shared/components/base/base-component/base-form.module';
import {
  CreatePackageSaleCommand,
  CustomResult,
  UpdatePackageSaleCommand,
} from '../../../../core/api/models';
import { FormPageConfig } from '../../../../core/models/form-page-config.model';
import {
  DialogService,
  DialogStatus,
} from '../../../../core/services/dialog.service';
import { AppDomService } from '../../../../core/services/dom.service';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';

@Component({
  selector: 'app-package-sales-form',
  templateUrl: './package-sales-form.component.html',
  imports: [BaseFormModule],
  styles: [''],
})
export class PackageSalesFormComponent
  extends BaseFormPageComponent
  implements OnInit, AfterViewInit
{
  url: string = AppConfigService.config.apiUrl;
  protected currentService = inject(PackageSalesService);
  //#region Properties
  formPageConfig: FormPageConfig<UpdatePackageSaleCommand> = {};
  model: CreatePackageSaleCommand = {};

  editorData = '';
  editorConfig;

  //#endregion

  constructor(
    public dialogService: DialogService,
    private fileType: FileTypePipe,
    private translate: TranslateService
  ) {
    super();
  }

  ngAfterViewInit(): void {
    try {
      this.onListenerOpenFileManager();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  onListenerOpenFileManager() {
    try {
      if (this.formPageConfig.isEditMode)
        setTimeout(() => {
          const el = document.querySelector(
            '[data-cke-tooltip-text="Block quote"]'
          );
          //create element
          const uploadFileElement = document.createElement('i');
          uploadFileElement.id = 'text-editor-upload';
          uploadFileElement.className +=
            'mdi mdi-upload mdi-20px tw-text-gray-900 dark:tw-text-white tw-mt-2 tw-px-[4px] upload-file-hover';
          //add element to DOM
          el.after(uploadFileElement);

          fromEvent(document.getElementById('text-editor-upload'), 'click')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((event) => {
              let data = {
                menuId: this.formPageConfig.editData.id,
              };

              const modalResult = this.dialogService.openModal({
                component: FileManagerComponent,
                data: data,
              });

              modalResult
                .afterClosed()
                .pipe(
                  takeUntil(this.onDestroy$),
                  tap(() => {
                    AppDomService.removeModalScrollClassFromHtml();
                  })
                )
                .subscribe((result) => {
                  if (result.file) this.getFile(result.file);
                  this.model = result.model;
                });
            });
        }, 200);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  ngOnInit() {
    try {
      super.ngOnInit();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  initForm() {
    this.form = new FormGroup({
      title: new FormControl('', Validators.required),
      originalPrice: new FormControl('', Validators.required),
      isActive: new FormControl(''),
      id: new FormControl(''),
      expirationType: new FormControl(''),
      expiration: new FormControl('', Validators.required),
      depositPrice: new FormControl('', Validators.required),
    });
  }

  //#region Api

  //Create
  baseCreateApi(model: CreatePackageSaleCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .packageSalesCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });
          if (result.isSucceed) {
            // this.uploadFiles(result.detail);

            this.formPageConfig.isEditMode = false;
            this.onSuccess();
          }
        },
        (error) => ConsoleService.error('error', error),
        () => (this.loading = false)
      );
  }

  //Update
  baseUpdateApi(model: UpdatePackageSaleCommand) {
    this.unSubs();

    this.loading = true;

    model.id = this.formPageConfig.editId;
    this.currentService
      .packageSalesUpdatePut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }
  //#endregion

  //#endregion

  onSubmit() {
    try {
      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;
      if (
        Number(this.form.get('originalPrice').value) <=
        Number(this.form.get('depositPrice').value)
      ) {
        this.dialogService.openDialog({
          title: 'common.error',
          message: this.translate.instant(
            'pages.packageSales.form.priceErrorMessage'
          ),
          status: DialogStatus.error,
        });
        return;
      }
      // this.model.claimId = this.model.claimId || null;
      //this.model.parentId = this.model.parentId || null;

      this.checkCreateOrUpdate();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  onReady(editor) {
    try {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return new Adapter(loader, editor.config);
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  getFile(file) {
    try {
      let type = this.fileType.transform(file);
      let fileUrl = this.url + '/' + file;

      if (type == 'image')
        this.editorData +=
          '<img style="height:20%;width:20%;" src=' + fileUrl + '>';
      else if (type == 'video')
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadVideo',
          { fileUrl }
        );
      else if (type == 'pdf')
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadPDF',
          { fileUrl }
        );
      else
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadFile',
          { fileUrl }
        );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }
}
